import React, { useState, useMemo } from 'react';
import {
  Baby, Plus, Trash2, ChevronDown, ChevronUp, Shield, Utensils,
  Phone, Heart, Users, Gamepad2, AlertTriangle, Calculator
} from 'lucide-react';

const AGE_GROUPS = [
  { id: 'baby', label: '0–2 years', desc: 'Baby / toddler' },
  { id: 'toddler', label: '3–5 years', desc: 'Toddler' },
  { id: 'child', label: '6–9 years', desc: 'Child' },
  { id: 'preteen', label: '10–13 years', desc: 'Tweens' },
];

const ACTIVITIES_BY_AGE = {
  'baby': [
    { id: 'soft-toys', label: 'Myke leker', price: 200 },
    { id: 'blankets', label: 'Teppe / sovearea', price: 0 },
    { id: 'baby-books', label: 'Board books', price: 150 },
  ],
  'toddler': [
    { id: 'coloring', label: 'Drawing supplies & colored pencils', price: 100 },
    { id: 'activity-books', label: 'Activeitetshefter', price: 80 },
    { id: 'playdough', label: 'Plastelina / lekeleire', price: 60 },
    { id: 'building-blocks', label: 'LEGO Duplo / byggeleker', price: 200 },
    { id: 'stickers', label: 'Sticker books', price: 50 },
    { id: 'movie-corner', label: 'Movie corner (tablet/TV)', price: 0 },
    { id: 'bubbles', label: 'Soap bubbles', price: 30 },
  ],
  'child': [
    { id: 'coloring', label: 'Drawing supplies & colored pencils', price: 100 },
    { id: 'lego', label: 'LEGO / byggesett', price: 300 },
    { id: 'board-games', label: 'Brettspill', price: 200 },
    { id: 'treasure-hunt', label: 'Skattejakt', price: 100 },
    { id: 'outdoor-games', label: 'Outdoorleker (fotball, frisbee)', price: 150 },
    { id: 'crafts', label: 'Hobbyverksted', price: 150 },
    { id: 'photo-props', label: 'Fotoboks for barn', price: 100 },
    { id: 'movie-corner', label: 'Movie corner', price: 0 },
    { id: 'card-games', label: 'Kortspill (UNO osv.)', price: 50 },
  ],
  'preteen': [
    { id: 'board-games', label: 'Brettspill / kortspill', price: 200 },
    { id: 'outdoor-games', label: 'Outdoorleker / sport', price: 150 },
    { id: 'music-quiz', label: 'Musikkquiz', price: 0 },
    { id: 'photo-booth', label: 'Fotoboks', price: 100 },
    { id: 'treasure-hunt', label: 'Skattejakt (avansert)', price: 150 },
    { id: 'craft-station', label: 'Hobbyverksted', price: 150 },
    { id: 'gaming', label: 'Nintendo Switch / spill', price: 0 },
  ],
};

const Inp = ({ label, value, onChange, placeholder, type = 'text', className = '' }) => (
  <div className={className}>
    <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase tracking-wide mb-0.5 block">{label}</label>
    <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
  </div>
);

export const KidsPlanner = ({ kids, onChange, guests }) => {
  const [expanded, setExpanded] = useState(null);

  // Auto-collect children from guest list
  const guestChildren = useMemo(() => {
    const list = [];
    (guests || []).forEach(g => {
      if (g.hasChildren && parseInt(g.childrenCount) > 0) {
        const ages = (g.childrenAges || '').split(',').map(a => a.trim()).filter(Boolean);
        for (let i = 0; i < parseInt(g.childrenCount); i++) {
          list.push({
            parentName: g.name || 'Ukjent',
            parentPhone: g.phone || '',
            age: ages[i] || '',
            allergies: g.allergies || '',
            meal: g.meal || 'standard',
            fromGuest: true,
          });
        }
      }
    });
    return list;
  }, [guests]);

  const childrenList = kids?.children || [];
  const totalChildren = Math.max(childrenList.length, guestChildren.length);

  // Sync guest children into kids list if not already
  React.useEffect(() => {
    if (guestChildren.length > 0 && childrenList.length === 0) {
      const imported = guestChildren.map((gc, i) => ({
        id: `child-${Date.now()}-${i}`,
        name: '',
        age: gc.age,
        parentName: gc.parentName,
        parentPhone: gc.parentPhone,
        allergies: gc.allergies,
        meal: gc.meal,
        needsHighChair: false,
        needsChanging: false,
        needsSleepArea: false,
      }));
      onChange({ ...kids, children: imported });
    }
  }, [guestChildren.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (field, value) => onChange({ ...kids, [field]: value });
  const updateChild = (idx, field, value) => {
    const updated = [...childrenList];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange({ ...kids, children: updated });
  };
  const addChild = () => {
    onChange({ ...kids, children: [...childrenList, {
      id: `child-${Date.now()}`, name: '', age: '', parentName: '', parentPhone: '',
      allergies: '', meal: 'standard', needsHighChair: false, needsChanging: false, needsSleepArea: false,
    }] });
  };
  const removeChild = (idx) => onChange({ ...kids, children: childrenList.filter((_, i) => i !== idx) });

  const toggleActivity = (actId) => {
    const current = kids?.activities || [];
    const exists = current.find(a => a.id === actId);
    if (exists) {
      update('activities', current.filter(a => a.id !== actId));
    } else {
      const allActs = Object.values(ACTIVITIES_BY_AGE).flat();
      const act = allActs.find(a => a.id === actId);
      update('activities', [...current, { id: actId, label: act?.label || actId, price: act?.price || 0 }]);
    }
  };

  // Age distribution
  const ageDist = useMemo(() => {
    const dist = { baby: 0, toddler: 0, child: 0, preteen: 0 };
    childrenList.forEach(c => {
      const age = parseInt(c.age);
      if (isNaN(age)) return;
      if (age <= 2) dist.baby++;
      else if (age <= 5) dist.toddler++;
      else if (age <= 9) dist.child++;
      else dist.preteen++;
    });
    return dist;
  }, [childrenList]);

  const relevantAgeGroups = AGE_GROUPS.filter(g => ageDist[g.id] > 0);
  const totalActivityCost = (kids?.activities || []).reduce((s, a) => s + (parseFloat(a.price) || 0), 0);

  return (
    <div data-testid="kids-planner" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-sans font-semibold text-stone-800 text-sm">Children at the wedding</h3>
          <p className="text-xs font-sans text-stone-500 mt-0.5">
            {guestChildren.length > 0 ? `${guestChildren.length} barn funnet i the guest list` : 'Planlegg for de yngste the guests'}
          </p>
          <p className="text-[10px] font-sans text-stone-400 mt-1 leading-relaxed">
            Children are imported from the guest list. Add age to get age-appropriate activity suggestions and cost estimates.
          </p>
        </div>
      </div>

      {guestChildren.length > 0 && childrenList.length === 0 && (
        <div className="bg-amber-50 rounded-xl border border-amber-200/50 p-4 flex items-start gap-3">
          <Baby className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-sans font-semibold text-amber-700">Child registered i the guest list</p>
            <p className="text-[10px] font-sans text-amber-600 mt-0.5">Data importeres automatisk fra the guest list.</p>
          </div>
        </div>
      )}

      {/* Children list */}
      <div className="space-y-2">
        {childrenList.map((child, idx) => (
          <div key={child.id || idx} className={`border rounded-xl transition-all ${expanded === idx ? 'border-primary/30 bg-white' : 'border-stone-200'}`}>
            <div className="flex items-center gap-2 p-3 cursor-pointer" onClick={() => setExpanded(expanded === idx ? null : idx)}>
              <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center text-[10px] font-bold text-amber-700">{idx + 1}</div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-sans font-medium text-stone-700 truncate">{child.name || `Child ${idx + 1}`}</p>
                <p className="text-[9px] font-sans text-stone-400">{child.age ? `${child.age} years` : ''} {child.parentName ? `• Foresatt: ${child.parentName}` : ''}</p>
              </div>
              <button type="button" onClick={e => { e.stopPropagation(); removeChild(idx); }} className="text-stone-300 hover:text-red-500 p-1 transition-colors">
                <Trash2 className="w-3 h-3" />
              </button>
              {expanded === idx ? <ChevronUp className="w-3.5 h-3.5 text-stone-400" /> : <ChevronDown className="w-3.5 h-3.5 text-stone-400" />}
            </div>
            {expanded === idx && (
              <div className="px-3 pb-3 border-t border-stone-100 space-y-3 pt-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <Inp label="Childets navn" value={child.name} onChange={v => updateChild(idx, 'name', v)} placeholder="Name" />
                  <Inp label="Age" value={child.age} onChange={v => updateChild(idx, 'age', v)} type="number" placeholder="Age" />
                  <Inp label="Foresatt" value={child.parentName} onChange={v => updateChild(idx, 'parentName', v)} placeholder="Foresatt" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Inp label="Foresatts telefon" value={child.parentPhone} onChange={v => updateChild(idx, 'parentPhone', v)} placeholder="Phone" />
                  <Inp label="Allrgyer" value={child.allergies} onChange={v => updateChild(idx, 'allergies', v)} placeholder="e.g. nuts" />
                </div>
                <div className="flex flex-wrap gap-3">
                  {[
                    { id: 'needsHighChair', label: 'Childestol' },
                    { id: 'needsChanging', label: 'Stellebehov' },
                    { id: 'needsSleepArea', label: 'Sove-/hvilearea' },
                  ].map(opt => (
                    <label key={opt.id} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={child[opt.id] || false} onChange={e => updateChild(idx, opt.id, e.target.checked)}
                        className="rounded border-stone-300 text-primary" />
                      <span className="text-[10px] font-sans text-stone-600">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <button type="button" onClick={addChild}
        className="w-full border-2 border-dashed border-stone-200 rounded-xl p-2.5 flex items-center justify-center gap-2 text-stone-400 hover:border-stone-300 hover:text-stone-500 transition-all text-xs font-sans font-medium">
        <Plus className="w-3.5 h-3.5" /> Add child
      </button>

      {/* Age distribution */}
      {childrenList.length > 0 && (
        <div className="bg-white rounded-2xl border border-stone-100 p-4">
          <h4 className="text-[10px] font-sans font-bold text-stone-500 uppercase tracking-wide mb-2">Agesfordeling</h4>
          <div className="flex flex-wrap gap-2">
            {AGE_GROUPS.map(g => (
              <div key={g.id} className={`px-3 py-2 rounded-xl border text-center ${ageDist[g.id] > 0 ? 'border-amber-200 bg-amber-50' : 'border-stone-100 bg-stone-50'}`}>
                <p className="text-sm font-sans font-bold text-stone-700">{ageDist[g.id]}</p>
                <p className="text-[9px] font-sans text-stone-400">{g.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Babysitter */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4 space-y-3">
        <h4 className="text-[10px] font-sans font-bold text-stone-500 uppercase tracking-wide">Babysitter</h4>
        <div className="flex gap-2 mb-2">
          {['Ja', 'Nei', 'Kanskje'].map(opt => (
            <button key={opt} type="button" onClick={() => update('hasBabysitter', opt)}
              className={`text-xs font-sans px-3 py-2 rounded-xl border transition-all ${
                kids?.hasBabysitter === opt ? 'bg-primary/10 border-primary text-primary font-semibold' : 'bg-white border-stone-200 text-stone-600 hover:border-stone-400'
              }`}>{opt}</button>
          ))}
        </div>
        {kids?.hasBabysitter === 'Ja' && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Inp label="Number of childrenevakter" value={kids?.babysitterCount} onChange={v => update('babysitterCount', v)} type="number" placeholder="1" />
            <Inp label="Contact person" value={kids?.babysitterContact} onChange={v => update('babysitterContact', v)} placeholder="Name" />
            <Inp label="Phone" value={kids?.babysitterPhone} onChange={v => update('babysitterPhone', v)} placeholder="Phone" />
          </div>
        )}
        <Inp label="Responsible for barna" value={kids?.responsiblePerson} onChange={v => update('responsiblePerson', v)} placeholder="Hvem koordinerer barneopplegget?" />
      </div>

      {/* Facilities */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4 space-y-3">
        <h4 className="text-[10px] font-sans font-bold text-stone-500 uppercase tracking-wide">Fasiliteter</h4>
        <div className="grid grid-cols-2 gap-2">
          <Inp label="Storom" value={kids?.changingRoom} onChange={v => update('changingRoom', v)} placeholder="Venue / lokale" />
          <Inp label="Sove/hvilearea" value={kids?.sleepArea} onChange={v => update('sleepArea', v)} placeholder="Venue / oppsett" />
          <Inp label="Childetransport" value={kids?.kidTransport} onChange={v => update('kidTransport', v)} placeholder="e.g. egne biler / shuttle" />
          <Inp label="Sikkerhetsvurdering" value={kids?.safetyNotes} onChange={v => update('safetyNotes', v)} placeholder="e.g. vannkant, trapper" />
        </div>
      </div>

      {/* Entertainment Planner */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-[10px] font-sans font-bold text-stone-500 uppercase tracking-wide">
            <Gamepad2 className="w-3 h-3 inline mr-1" /> Kids Entertainment Planner
          </h4>
          {totalActivityCost > 0 && (
            <span className="text-[10px] font-sans font-bold text-primary flex items-center gap-1">
              <Calculator className="w-3 h-3" /> {totalActivityCost.toLocaleString('en-US')} kr estimert
            </span>
          )}
        </div>
        {relevantAgeGroups.length > 0 ? (
          <div className="space-y-4">
            {relevantAgeGroups.map(g => (
              <div key={g.id}>
                <p className="text-xs font-sans font-semibold text-stone-700 mb-2">{g.label} ({ageDist[g.id]} barn)</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                  {(ACTIVITIES_BY_AGE[g.id] || []).map(act => {
                    const active = (kids?.activities || []).some(a => a.id === act.id);
                    return (
                      <button key={act.id} type="button" onClick={() => toggleActivity(act.id)}
                        className={`px-2.5 py-2 rounded-lg border text-left text-[10px] font-sans transition-all ${
                          active ? 'bg-primary/10 border-primary text-primary font-semibold' : 'border-stone-200 text-stone-600 hover:border-stone-300'
                        }`}>
                        {act.label}
                        {act.price > 0 && <span className="text-stone-400 ml-1">~{act.price} kr</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs font-sans text-stone-400">Add children with age to get activity suggestions.</p>
        )}
      </div>
    </div>
  );
};
